import React from "react";
import { Trash2, X, Check } from "lucide-react";

/**
 * Sticky action bar shown when at least one item is selected.
 * Props:
 *   selectedCount: number
 *   totalCount: number  (for select-all)
 *   allSelected: boolean
 *   onToggleAll: () => void
 *   onClear: () => void
 *   onBulkDelete: () => void
 *   disabled?: boolean
 *   label?: string (e.g. "articles", "membres", "suggestions")
 */
export default function BulkActionBar({
    selectedCount,
    totalCount,
    allSelected,
    onToggleAll,
    onClear,
    onBulkDelete,
    disabled = false,
    label = "éléments",
}) {
    if (selectedCount === 0) return null;
    return (
        <div
            className="sticky top-2 z-30 border p-3 flex items-center justify-between gap-3 flex-wrap"
            style={{
                borderColor: "var(--bc-gold)",
                background: "rgba(15,15,15,0.95)",
                backdropFilter: "blur(8px)",
            }}
            data-testid="bulk-action-bar"
        >
            <div className="flex items-center gap-3 flex-wrap">
                <span className="text-sm text-[#D4BC7C] font-medium" data-testid="bulk-selected-count">
                    {selectedCount} {label}{selectedCount > 1 ? "s" : ""} sélectionné{selectedCount > 1 ? "s" : ""}
                </span>
                {totalCount != null && (
                    <button
                        type="button"
                        onClick={onToggleAll}
                        className="text-xs text-[#F2ECE0] hover:text-[#D4BC7C] inline-flex items-center gap-1.5"
                        data-testid="bulk-toggle-all"
                    >
                        <Check size={12} />
                        {allSelected ? "Tout désélectionner" : `Tout sélectionner (${totalCount})`}
                    </button>
                )}
            </div>
            <div className="flex items-center gap-2">
                <button
                    type="button"
                    className="bc-btn bc-btn--sm bc-btn--ghost"
                    onClick={onClear}
                    disabled={disabled}
                    data-testid="bulk-clear"
                >
                    <X size={14} /> Effacer
                </button>
                <button
                    type="button"
                    className="bc-btn bc-btn--sm bc-btn--danger"
                    onClick={onBulkDelete}
                    disabled={disabled}
                    data-testid="bulk-delete"
                >
                    <Trash2 size={14} /> Supprimer la sélection
                </button>
            </div>
        </div>
    );
}

/**
 * Small themed checkbox to toggle selection state per row.
 */
export function SelectCheckbox({ checked, onChange, testId, label = "Sélectionner", stopPropagation = true }) {
    return (
        <label
            className="inline-flex items-center cursor-pointer"
            onClick={(e) => stopPropagation && e.stopPropagation()}
        >
            <input
                type="checkbox"
                className="h-4 w-4 accent-[#D4BC7C]"
                checked={!!checked}
                onChange={(e) => onChange(e.target.checked)}
                aria-label={label}
                data-testid={testId}
            />
        </label>
    );
}
